'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import InnerPageHeader from '@/components/InnerPageHeader';
import Toast from '@/components/Toast';

export default function GymSignup() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Gym Details
    firstName: '',
    businessName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    location: '',
    
    // Step 2: About & Photos
    aboutGym: '',
    photos: [] as string[],
    
    // Step 3: Services & Membership
    services: [] as string[],
    membershipPricing: '',
    membershipDescription: '',
    
    // Step 4: Operating Hours
    operatingHours: {
      Sunday: { isOpen: false, openTime: '9:00 AM', closeTime: '5:00 PM' },
      Monday: { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' },
      Tuesday: { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' },
      Wednesday: { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' },
      Thursday: { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' },
      Friday: { isOpen: true, openTime: '9:00 AM', closeTime: '5:00 PM' },
      Saturday: { isOpen: false, openTime: '9:00 AM', closeTime: '5:00 PM' },
    },
    
    // Step 5: Facility Features
    facilityFeatures: ['Women-Only Policy'] as string[],
  });

  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    
    // Validate file types
    const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    const invalidFiles = newFiles.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setToast({ message: 'Please upload only PNG, JPG, JPEG, or WEBP images', type: 'error' });
      return;
    }

    // Validate file size (max 5MB per file)
    const oversizedFiles = newFiles.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setToast({ message: 'Each image must be less than 5MB', type: 'error' });
      return;
    }

    // Compress and convert to base64
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for compression
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Max dimensions (resize if larger)
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx?.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression (0.7 quality)
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          
          setFormData(prev => ({
            ...prev,
            photos: [...prev.photos, compressedBase64]
          }));
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    });

    setPhotoFiles(prev => [...prev, ...newFiles]);
    e.target.value = ''; // Reset input
  };

  const handleRemovePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleDayToggle = (day: string) => {
    setFormData(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day as keyof typeof prev.operatingHours],
          isOpen: !prev.operatingHours[day as keyof typeof prev.operatingHours].isOpen
        }
      }
    }));
  };

  const handleTimeChange = (day: string, field: 'openTime' | 'closeTime', value: string) => {
    setFormData(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day as keyof typeof prev.operatingHours],
          [field]: value
        }
      }
    }));
  };

  // Generate time options
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let min = 0; min < 60; min += 30) {
        const h = hour % 12 || 12;
        const period = hour < 12 ? 'AM' : 'PM';
        const time = `${h}:${min.toString().padStart(2, '0')} ${period}`;
        times.push(time);
      }
    }
    return times;
  };

  const handleFeatureToggle = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      facilityFeatures: prev.facilityFeatures.includes(feature)
        ? prev.facilityFeatures.filter(f => f !== feature)
        : [...prev.facilityFeatures, feature]
    }));
  };

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation for Step 1
    if (currentStep === 1) {
      if (!formData.firstName || !formData.businessName || !formData.email || !formData.password || !formData.confirmPassword || !formData.phone || !formData.location) {
        setToast({ message: 'Please fill in all required fields', type: 'error' });
        return;
      }
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setToast({ message: 'Please enter a valid email address', type: 'error' });
        return;
      }

      // Password validation
      if (formData.password.length < 8) {
        setToast({ message: 'Password must be at least 8 characters', type: 'error' });
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setToast({ message: 'Passwords do not match', type: 'error' });
        return;
      }

      // Check email uniqueness
      try {
        const response = await fetch('/api/auth/check-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email }),
        });
        
        const data = await response.json();
        
        if (!response.ok || data.exists) {
          setToast({ message: 'This email is already registered. Please use a different email or login.', type: 'error' });
          return;
        }
      } catch (error) {
        setToast({ message: 'Failed to verify email. Please try again.', type: 'error' });
        return;
      }
    }

    // Validation for Step 2
    if (currentStep === 2) {
      if (!formData.aboutGym || formData.aboutGym.trim().length < 50) {
        setToast({ message: 'Please write at least 50 characters about your gym', type: 'error' });
        return;
      }
      if (formData.photos.length === 0) {
        setToast({ message: 'Please upload at least one photo of your gym', type: 'error' });
        return;
      }
    }

    // Validation for Step 3
    if (currentStep === 3) {
      if (formData.services.length === 0) {
        setToast({ message: 'Please select at least one service', type: 'error' });
        return;
      }
      if (!formData.membershipDescription || formData.membershipDescription.trim().length < 20) {
        setToast({ message: 'Please write at least 20 characters for membership description', type: 'error' });
        return;
      }
    }

    // Move to next step
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final submission - Submit to API
      try {
        const response = await fetch('/api/gym/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const data = await response.json();

        if (!response.ok) {
          const errorMsg = data.details 
            ? `${data.error} - ${data.details}` 
            : data.error || 'Failed to register gym. Please try again.';
          console.error('API Error:', data);
          setToast({ message: errorMsg, type: 'error' });
          return;
        }

        // Success - show modal
        console.log('Gym registered successfully:', data);
        setShowSuccessModal(true);
      } catch (error) {
        console.error('Registration error:', error);
        setToast({ 
          message: 'Failed to register gym. Please check your connection and try again.', 
          type: 'error' 
        });
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <InnerPageHeader />

      <section className="py-20">
        <div className="max-w-[1200px] mx-auto px-[20px]">
          {/* Eyebrow & Title */}
          <p className="font-['Archivo',sans-serif] text-[14px] text-[#8a7296] font-medium mb-4 tracking-wider">
            • GYM REGISTRATION
          </p>

          <h1 className="font-['Gotham_Rounded',sans-serif] text-[48px] font-bold text-[#2E2A3A] mb-3">
            Gym Details
          </h1>

          <p className="font-['Archivo',sans-serif] text-[18px] text-[#6e6672] mb-12">
            Step {currentStep} of 5 - Tell us about your gym
          </p>

          {/* Step 1: Gym Details */}
          {currentStep === 1 && (
            <form onSubmit={handleNext} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="e.g. John Doe"
                    required
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                  />
                </div>

                {/* Business Name */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Gym Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleInputChange}
                    placeholder="e.g. FitZone Women's Gym"
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Email address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="e.g. you@example.com"
                    required
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Minimum 8 characters"
                    required
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                  />
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Re-enter password"
                    required
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="e.g. 000 000 0000"
                    required
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                  />
                </div>
              </div>

              {/* Location - Full Width */}
              <div>
                <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="e.g. Birmingham, UK"
                  required
                  className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-6">
                <button
                  type="button"
                  onClick={() => router.push('/')}
                  className="px-6 py-3 font-['Archivo',sans-serif] text-[16px] text-[#6e6672] hover:text-[#2E2A3A] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors flex items-center gap-2"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </form>
          )}

          {/* Step 2: About & Photos */}
          {currentStep === 2 && (
            <form onSubmit={handleNext} className="space-y-6">
              {/* About The Gym */}
              <div>
                <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                  About The Gym <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="aboutGym"
                  value={formData.aboutGym}
                  onChange={handleInputChange}
                  placeholder="Type about your gym...."
                  rows={6}
                  required
                  className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors resize-none"
                />
                <p className="mt-1 font-['Archivo',sans-serif] text-[12px] text-[#6e6672]">
                  Minimum 50 characters ({formData.aboutGym.length}/50)
                </p>
              </div>

              {/* Upload Photos */}
              <div>
                <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-4">
                  Upload Photos of Your Gym <span className="text-red-500">*</span>
                </label>

                {/* Photo Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Uploaded Photos */}
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img
                        src={photo}
                        alt={`Gym photo ${index + 1}`}
                        className="w-full h-full object-cover rounded-[10px]"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white w-6 h-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-sm"
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {/* Upload Box */}
                  <label className="aspect-square border-2 border-dashed border-[#d7d7d7] rounded-[10px] flex flex-col items-center justify-center cursor-pointer hover:border-[#8a7296] transition-colors bg-[#f9f9f9]">
                    <input
                      type="file"
                      accept="image/png,image/jpg,image/jpeg,image/webp"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                    <span className="text-4xl mb-2">🖼️</span>
                    <p className="font-['Archivo',sans-serif] text-[12px] text-center px-2">
                      <strong>Drop your image here, or <span className="text-[#8a7296]">Browse</span></strong>
                    </p>
                    <small className="font-['Archivo',sans-serif] text-[10px] text-[#6e6672] mt-1">
                      PNG, JPG, JPEG, WEBP
                    </small>
                  </label>
                </div>

                <p className="mt-2 font-['Archivo',sans-serif] text-[12px] text-[#6e6672]">
                  Upload at least 1 photo (max 5MB per image)
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-6">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors flex items-center gap-2"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Services & Membership */}
          {currentStep === 3 && (
            <form onSubmit={handleNext} className="space-y-8">
              {/* Services We Offer */}
              <div>
                <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-4">
                  Services We Offer <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-3">
                  {[
                    'Yoga Classes',
                    'Pilates',
                    'Breathwork',
                    '1-to-1 Training',
                    'Parental Yoga',
                    'Workshops',
                    'Other'
                  ].map((service) => (
                    <button
                      key={service}
                      type="button"
                      onClick={() => handleServiceToggle(service)}
                      className={`px-4 py-2 rounded-full font-['Archivo',sans-serif] text-[14px] font-medium transition-all ${
                        formData.services.includes(service)
                          ? 'bg-[#8a7296] text-white'
                          : 'bg-white border-2 border-[#d7d7d7] text-[#2E2A3A] hover:border-[#8a7296]'
                      }`}
                    >
                      {service} {formData.services.includes(service) ? '✓' : '+'}
                    </button>
                  ))}
                </div>
                <p className="mt-2 font-['Archivo',sans-serif] text-[12px] text-[#6e6672]">
                  Selected: {formData.services.length} service{formData.services.length !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Membership Section */}
              <div className="pt-6 border-t border-[#d7d7d7]">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[24px] font-bold text-[#2E2A3A] mb-6">
                  Membership
                </h3>

                {/* Membership Pricing */}
                <div className="mb-6">
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Membership pricing <span className="text-[#6e6672] font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="membershipPricing"
                    value={formData.membershipPricing}
                    onChange={handleInputChange}
                    placeholder="e.g. £ 20/month"
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                  />
                </div>

                {/* Membership Description */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Membership Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="membershipDescription"
                    value={formData.membershipDescription}
                    onChange={handleInputChange}
                    placeholder="Type your description here ...."
                    rows={5}
                    required
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors resize-none"
                  />
                  <p className="mt-1 font-['Archivo',sans-serif] text-[12px] text-[#6e6672]">
                    Minimum 20 characters ({formData.membershipDescription.length}/20)
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-6">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors flex items-center gap-2"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </form>
          )}

          {/* Step 4: Operating Hours */}
          {currentStep === 4 && (
            <form onSubmit={handleNext} className="space-y-4">
              {Object.entries(formData.operatingHours).map(([day, hours]) => (
                <div
                  key={day}
                  className={`flex items-center gap-4 p-4 rounded-[10px] border-2 transition-colors ${
                    hours.isOpen ? 'border-[#8a7296] bg-[#8a72961a]' : 'border-[#d7d7d7] bg-white'
                  }`}
                >
                  {/* Day Name */}
                  <span className="font-['Archivo',sans-serif] text-[16px] font-medium text-[#2E2A3A] w-28">
                    {day}
                  </span>

                  {/* Toggle Switch */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hours.isOpen}
                      onChange={() => handleDayToggle(day)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-[#d7d7d7] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#8a7296]"></div>
                  </label>

                  {/* Status */}
                  <span className={`font-['Archivo',sans-serif] text-[14px] font-medium w-16 ${
                    hours.isOpen ? 'text-[#8a7296]' : 'text-[#6e6672]'
                  }`}>
                    {hours.isOpen ? 'Open' : 'Closed'}
                  </span>

                  {/* Time Selectors (only show if open) */}
                  {hours.isOpen && (
                    <>
                      <select
                        value={hours.openTime}
                        onChange={(e) => handleTimeChange(day, 'openTime', e.target.value)}
                        className="px-3 py-2 border border-[#d7d7d7] rounded-[12px] font-['Archivo',sans-serif] text-[14px] focus:outline-none focus:border-[#8a7296] bg-white"
                      >
                        {generateTimeOptions().map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>

                      <span className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] font-medium">
                        TO
                      </span>

                      <select
                        value={hours.closeTime}
                        onChange={(e) => handleTimeChange(day, 'closeTime', e.target.value)}
                        className="px-3 py-2 border border-[#d7d7d7] rounded-[12px] font-['Archivo',sans-serif] text-[14px] focus:outline-none focus:border-[#8a7296] bg-white"
                      >
                        {generateTimeOptions().map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              ))}

              {/* Actions */}
              <div className="flex justify-between items-center pt-6">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors flex items-center gap-2"
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </form>
          )}

          {/* Step 5: Facility Features */}
          {currentStep === 5 && (
            <form onSubmit={handleNext} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Feature Card 1: Women-Only Policy */}
                <label 
                  className={`relative p-6 rounded-[16px] border-2 cursor-pointer transition-all ${
                    formData.facilityFeatures.includes('Women-Only Policy')
                      ? 'border-[#8a7296] bg-[#8a72961a]'
                      : 'border-[#d7d7d7] bg-white hover:border-[#8a7296]'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.facilityFeatures.includes('Women-Only Policy')}
                    onChange={() => handleFeatureToggle('Women-Only Policy')}
                    className="hidden"
                  />
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">🔒</span>
                      <span className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                        Women-Only Policy
                      </span>
                    </div>
                    {formData.facilityFeatures.includes('Women-Only Policy') ? (
                      <span className="text-[#8a7296] text-xl">✔</span>
                    ) : (
                      <span className="w-6 h-6 border-2 border-[#d7d7d7] rounded"></span>
                    )}
                  </div>
                  <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] leading-relaxed">
                    All staff and clients are women. Door policy enforced at all times, no exceptions.
                  </p>
                </label>

                {/* Feature Card 2: Fully Accessible */}
                <label 
                  className={`relative p-6 rounded-[16px] border-2 cursor-pointer transition-all ${
                    formData.facilityFeatures.includes('Fully Accessible')
                      ? 'border-[#8a7296] bg-[#8a72961a]'
                      : 'border-[#d7d7d7] bg-white hover:border-[#8a7296]'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.facilityFeatures.includes('Fully Accessible')}
                    onChange={() => handleFeatureToggle('Fully Accessible')}
                    className="hidden"
                  />
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">♿</span>
                      <span className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                        Fully Accessible
                      </span>
                    </div>
                    {formData.facilityFeatures.includes('Fully Accessible') ? (
                      <span className="text-[#8a7296] text-xl">✔</span>
                    ) : (
                      <span className="w-6 h-6 border-2 border-[#d7d7d7] rounded"></span>
                    )}
                  </div>
                  <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] leading-relaxed">
                    Step-free access, accessible changing rooms, and adaptive class options available.
                  </p>
                </label>

                {/* Feature Card 3: Complete Privacy */}
                <label 
                  className={`relative p-6 rounded-[16px] border-2 cursor-pointer transition-all ${
                    formData.facilityFeatures.includes('Complete Privacy')
                      ? 'border-[#8a7296] bg-[#8a72961a]'
                      : 'border-[#d7d7d7] bg-white hover:border-[#8a7296]'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.facilityFeatures.includes('Complete Privacy')}
                    onChange={() => handleFeatureToggle('Complete Privacy')}
                    className="hidden"
                  />
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">🛡️</span>
                      <span className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                        Complete Privacy
                      </span>
                    </div>
                    {formData.facilityFeatures.includes('Complete Privacy') ? (
                      <span className="text-[#8a7296] text-xl">✔</span>
                    ) : (
                      <span className="w-6 h-6 border-2 border-[#d7d7d7] rounded"></span>
                    )}
                  </div>
                  <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] leading-relaxed">
                    All windows frosted or covered. No external visibility into any studio or changing space.
                  </p>
                </label>

                {/* Feature Card 4: CCTV Monitoring */}
                <label 
                  className={`relative p-6 rounded-[16px] border-2 cursor-pointer transition-all ${
                    formData.facilityFeatures.includes('CCTV Monitoring')
                      ? 'border-[#8a7296] bg-[#8a72961a]'
                      : 'border-[#d7d7d7] bg-white hover:border-[#8a7296]'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.facilityFeatures.includes('CCTV Monitoring')}
                    onChange={() => handleFeatureToggle('CCTV Monitoring')}
                    className="hidden"
                  />
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">📷</span>
                      <span className="font-['Gotham_Rounded',sans-serif] text-[18px] font-bold text-[#2E2A3A]">
                        CCTV Monitoring
                      </span>
                    </div>
                    {formData.facilityFeatures.includes('CCTV Monitoring') ? (
                      <span className="text-[#8a7296] text-xl">✔</span>
                    ) : (
                      <span className="w-6 h-6 border-2 border-[#d7d7d7] rounded"></span>
                    )}
                  </div>
                  <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] leading-relaxed">
                    24/7 security monitoring for member safety. Cameras in common areas only.
                  </p>
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-6">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-3 border border-[#d7d7d7] text-[#2E2A3A] font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#F6EFED] transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors flex items-center gap-2"
                >
                  Finish
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </form>
          )}

          {/* Progress Indicator */}
          <div className="mt-12 pt-8 border-t border-[#d7d7d7]">
            <div className="flex justify-between items-center mb-2">
              <span className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                Progress
              </span>
              <span className="font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">
                {currentStep} of 5
              </span>
            </div>
            <div className="w-full bg-[#e5e5e5] rounded-full h-2">
              <div
                className="bg-[#8a7296] h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / 5) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[24px] max-w-[500px] w-full p-8 relative animate-in fade-in zoom-in duration-300">
            {/* Close Button */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-[#6e6672] hover:text-[#2E2A3A] text-2xl transition-colors"
            >
              ×
            </button>

            {/* Success Icon */}
            <div className="w-24 h-24 mx-auto mb-6">
              <img 
                src="/listed-done.jpg" 
                alt="Success" 
                className="w-full h-full object-contain"
              />
            </div>

            {/* Title */}
            <h2 className="font-['Gotham_Rounded',sans-serif] text-[28px] font-bold text-[#2E2A3A] text-center mb-4">
              Bloom Gym is listed!
            </h2>

            {/* Description */}
            <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672] text-center leading-relaxed mb-8">
              Your gym is now listed on the platform. Women near you can discover and book visits. Please wait for admin approval to make your listing live.
            </p>

            {/* Dashboard Button */}
            <button
              onClick={() => router.push('/dashboard')}
              className="w-full px-8 py-4 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors flex items-center justify-center gap-2"
            >
              Go To Dashboard
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
